import { type HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntities, type EntityId, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { type Observable } from 'rxjs';

import { createStaleResponseGuard } from './stale-response-guard';

export interface PaginatedLoadState<TQuery> {
  count: number;
  error: HttpErrorResponse | null;
  hasMore: boolean;
  isLoading: boolean;
  query: TQuery | null;
}

export interface PaginatedLoadPage<TRow> {
  count?: number | null;
  data?: TRow[] | null;
}

export interface PaginatedLoadConfig<TRow, TQuery> {
  /** Only consulted by `loadAll` - defaults to `JSON.stringify(query)` when omitted; `null` skips the cache. */
  cacheKey?: (query: TQuery) => string | null;
  fetchPage: (query: TQuery, offset: number) => Observable<PaginatedLoadPage<TRow>>;
  onError?: (error: HttpErrorResponse) => void;
}

/** Every `TRow` needs a stable `id` - required by `withEntities`, and the natural fit for a
 * paginated resource anyway (a backend page is keyed by id even before it reaches the client). */
export function withPaginatedLoad<TRow extends { id: EntityId }, TQuery>(configFactory: () => PaginatedLoadConfig<TRow, TQuery>) {
  return signalStoreFeature(
    withEntities<TRow>(),
    withState<PaginatedLoadState<TQuery>>({
      count: 0,
      error: null,
      hasMore: false,
      isLoading: false,
      query: null,
    }),
    // `rows` is the array view most consumers actually want (mat-table dataSource, client-side
    // aggregation) - `entities` from `withEntities` is the same data, just named for the feature
    // that owns it.
    withComputed(({ entities }) => ({ rows: entities })),
    withMethods(store => {
      const config = configFactory();
      const requestGuard = createStaleResponseGuard();
      // Keyed by JSON.stringify(query) - only `loadAll` populates this, for consumers that need
      // the full result set (e.g. client-side aggregation) rather than incremental scroll pages.
      const fullLoadCache = new Map<string, { count: number; rows: TRow[] }>();

      function loadPage(replace: boolean, onSettled?: () => void) {
        const query = store.query();

        if (query === null) {
          return;
        }

        const offset = replace ? 0 : store.rows().length;

        patchState(store, { error: null, isLoading: true });

        const requestId = requestGuard.start();

        config.fetchPage(query, offset).subscribe({
          error: (error: HttpErrorResponse) => {
            if (!requestGuard.isLatest(requestId)) {
              return;
            }

            patchState(store, { error, isLoading: false });
            config.onError?.(error);
          },
          next: ({ count, data }) => {
            if (!requestGuard.isLatest(requestId)) {
              return;
            }

            const newRows = data ?? [];
            const total = count ?? 0;

            patchState(store, replace ? setAllEntities(newRows) : addEntities(newRows));

            // Read back after the patch - `addEntities` dedups by id, so the row count isn't
            // predictable from `newRows.length` alone.
            patchState(store, {
              count: total,
              hasMore: store.rows().length < total,
              isLoading: false,
            });

            onSettled?.();
          },
        });
      }

      return {
        /**
         * Pages through every result for `query` (small requests, not one huge one), then caches
         */
        loadAll(query: TQuery) {
          patchState(store, { query });
          // Bumps the guard even on a cache hit, so a still-in-flight response from a previous
          requestGuard.start();

          const cacheKey = config.cacheKey ? config.cacheKey(query) : JSON.stringify(query);
          const cached = cacheKey === null ? undefined : fullLoadCache.get(cacheKey);

          if (cached) {
            patchState(store, setAllEntities(cached.rows), {
              count: cached.count,
              error: null,
              hasMore: false,
              isLoading: false,
            });

            return;
          }

          let loadedRows = 0;

          function step() {
            if (store.hasMore()) {
              /* A page that adds no new rows would loop forever (rows inserted mid-paging); settle without caching. */
              if (store.rows().length === loadedRows) {
                patchState(store, { hasMore: false });

                return;
              }

              loadedRows = store.rows().length;
              loadPage(false, step);

              return;
            }

            if (cacheKey !== null) {
              fullLoadCache.set(cacheKey, { count: store.count(), rows: store.rows() });
            }
          }

          loadPage(true, step);
        },
        loadNextPage() {
          if (!store.hasMore() || store.isLoading()) {
            return;
          }

          loadPage(false);
        },
        reload() {
          loadPage(true);
        },
        setQuery(query: TQuery) {
          patchState(store, { query });
          loadPage(true);
        },
      };
    }),
  );
}
